import { getDiscountProviderProfile } from "./providers";
import type { DiscountProviderProfile, VerifyChannel } from "./providers";
import type { DiscountOffer } from "./types";

/**
 * Provider-level rules: what the coupon partner in play permits.
 *
 * 🔴 **No rule here tests a `metaId`.** Each one asks that provider's capability profile
 * (`./providers`) and applies a rule that is the same for every provider. Adding a partner is a
 * profile; changing what a capability MEANS is a rule here. Keeping those two separate is what
 * stops the flow re-accumulating `if (isExternalProviderC1(...))`.
 *
 * Pure — arguments in, answer out. Nothing here reads the store.
 */

/** Re-exported so a caller with a `metaId` can read any capability without a second import. */
export { getDiscountProviderProfile };
export type { DiscountProviderProfile };

/**
 * Is this partner one whose offers are fetched and applied THROUGH the partner?
 *
 * 🔴 **The gate for the whole external leg** — the offers fetch, the partner apply, the OTP
 * conversation and the removal unblock all hang off it. A provider that answers `false` runs the
 * ordinary coupon path end to end, which is what an unknown `metaId` gets.
 *
 * Replaces `isExternalLoyaltyProvider(metaId)`.
 */
export const isExternalCouponProvider = (metaId?: string): boolean =>
  getDiscountProviderProfile(metaId).isExternalLoyalty;

/**
 * Must a coupon this partner issued be released server-side when it comes off the bill?
 *
 * ⚠️ Answered per PARTNER, and the partner is read off the DISCOUNT (`couponProvider`) before
 * falling back to the active provider — see `resolveDiscountProvider`. A bill can carry a coupon
 * from a partner that is no longer the active one.
 */
export const requiresCouponUnblock = (metaId?: string): boolean =>
  getDiscountProviderProfile(metaId).requiresCouponUnblock;

/**
 * What the unblock call needs before it is worth sending.
 *
 * `{ needsReferenceAndPhone: true }` → a missing `referenceId` or customer phone means the
 * partner will reject it, so the caller reports and skips. Replaces the inline
 * `isExternalProviderC1(metaId)` guard in `removeExternalDiscount`.
 */
export const getUnblockRequirements = (
  metaId?: string,
): { needsReferenceAndPhone: boolean } => ({
  needsReferenceAndPhone: getDiscountProviderProfile(metaId).unblockRequiresReferenceAndPhone,
});

/**
 * The code an offer row should DISPLAY.
 *
 * ⚠️ Falls back to the other field, then to `mid`. A partner that declares `couponCode` but
 * omits it on a particular offer would otherwise render a blank row, and a blank code is
 * indistinguishable from a broken list.
 */
export const getOfferCode = (offer: DiscountOffer | null | undefined): string => {
  const profile = getDiscountProviderProfile(offer?.couponProvider);
  const preferred = offer?.[profile.offerCodeField];
  const fallback =
    profile.offerCodeField === "couponCode" ? offer?.discountCode : offer?.couponCode;
  return String(preferred || fallback || offer?.mid || "");
};

/**
 * The configured partner the customer coupon modal serves, or `undefined` — and then nothing
 * offers the modal. The FIRST with `hasCouponModal`, as v1 took the first external partner.
 *
 * One question for its four web sites: the cart header's icon and the customer modal's row ask
 * whether there is one; the modal and that row's points line ask which. All four must ask it the
 * same way — a gate on one predicate and a pick on another would open the modal on DotPe for a
 * merchant with DotPe and Xeno.
 *
 * `= any` because the apps hand in `businessData.loyaltyProviders` untyped, and inference from
 * `any` falls back to the constraint — `{ metaId }` alone, with no `mid` to fetch by.
 */
export const findCouponModalProvider = <T extends { metaId?: string } = any>(
  loyaltyProviders?: T[] | null,
): T | undefined =>
  (loyaltyProviders ?? []).find(
    (provider) => getDiscountProviderProfile(provider?.metaId).hasCouponModal,
  );

/** Does this partner return per-item discounts that must be stamped onto the sale? */
export const returnsItemLevelDiscounts = (metaId?: string): boolean =>
  getDiscountProviderProfile(metaId).returnsItemLevelDiscounts;

/**
 * If the recalculated bill shows the coupon reduced nothing, must it be taken back off?
 *
 * Read only on the `!applied` branch of an apply. `false` leaves the entry for the pricing
 * engine to keep ignoring, which is what a catalog deal does.
 */
export const shouldRollBackUnappliedCoupon = (metaId?: string): boolean =>
  getDiscountProviderProfile(metaId).rollsBackUnappliedCoupon;

/** Does the bill-discount entry for this partner carry a `requestId`? */
export const sendsRequestId = (metaId?: string): boolean =>
  getDiscountProviderProfile(metaId).sendsRequestId;

/** Which API this partner's OTP verification goes through. */
export const getVerifyChannel = (metaId?: string): VerifyChannel =>
  getDiscountProviderProfile(metaId).verifyChannel;

/**
 * After a successful verify, should the sale's `customerInfo` be stamped as
 * externally-authenticated?
 *
 * `true` records that the CUSTOMER was verified by the partner's OTP. A partner whose OTP
 * verifies the OFFER answers `false` — see `xenoProfile`.
 */
export const stampsExternalAuthentication = (metaId?: string): boolean =>
  getDiscountProviderProfile(metaId).stampsExternalAuthentication;

/**
 * The partner an already-applied discount belongs to.
 *
 * 🔴 **The discount's own `couponProvider` wins over the active provider.** A bill can carry a
 * coupon from a partner the merchant has since switched away from, and every later call about
 * that coupon — the unblock in particular — has to reach the partner that issued it. Falling
 * straight through to the active provider sends the release to the wrong partner and leaves the
 * customer's coupon blocked forever.
 */
export const resolveDiscountProvider = (
  discount: { couponProvider?: string } | null | undefined,
  activeProviderMetaId?: string,
): string | undefined => discount?.couponProvider || activeProviderMetaId;
